'use client';

import React from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import { Container } from '@/components/client/Container/Container';
import { Section } from '@/components/client/Section/Section';
import { AuthorPostCard } from '@/features/client/Blog/AuthorPostCard';
import { Author } from '@/data/authors';
import { ISmallCardItem } from '@/features/client/Blog/SmallCard';

interface IProps {
  author: Author;
  posts: ISmallCardItem[];
}

export const AuthorPage = ({ author, posts }: IProps) => {
  const hasSocial =
    author.social.linkedin || author.social.facebook || author.social.instagram;

  return (
    <>
      <div className="h-9" />
      <Section
        variant="primary"
        styles={{
          paddingTop: 36,
          paddingBottom: 72,
          textAlign: 'left' as const,
        }}
      >
        <Container>
          <PageWrap>
            {/* <AccentRule aria-hidden /> */}
            <ProfileGrid>
              <ProfileCard>
                <PhotoWrapper>
                  <Photo
                    src={author.photo}
                    alt={author.name}
                    width={200}
                    height={200}
                    priority
                  />
                </PhotoWrapper>
                <AuthorName>{author.name}</AuthorName>
                <AuthorTitle>{author.jobTitle}</AuthorTitle>
                <AuthorLocation>{author.location}</AuthorLocation>
                {hasSocial && (
                  <SocialRow>
                    {author.social.linkedin && (
                      <SocialLink
                        href={author.social.linkedin}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        aria-label="LinkedIn"
                      >
                        <LinkedInIcon />
                      </SocialLink>
                    )}
                    {author.social.facebook && (
                      <SocialLink
                        href={author.social.facebook}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        aria-label="Facebook"
                      >
                        <FacebookIcon />
                      </SocialLink>
                    )}
                    {author.social.instagram && (
                      <SocialLink
                        href={author.social.instagram}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        aria-label="Instagram"
                      >
                        <InstagramIcon />
                      </SocialLink>
                    )}
                  </SocialRow>
                )}
              </ProfileCard>

              <InfoPanel>
                <SectionLabel>
                  {/* <SectionLabelAccent /> */}
                  Profile Information
                </SectionLabel>
                <Bio>{author.bio}</Bio>
                <MetaCard>
                  <InfoTable>
                    <tbody>
                      <tr>
                        <InfoKey>Job Title</InfoKey>
                        <InfoValue>{author.jobTitle}</InfoValue>
                      </tr>
                      <tr>
                        <InfoKey>Company</InfoKey>
                        <InfoValue>{author.company}</InfoValue>
                      </tr>
                      <tr>
                        <InfoKey>Location</InfoKey>
                        <InfoValue>{author.location}</InfoValue>
                      </tr>
                      <tr>
                        <InfoKey>Favorite Topics</InfoKey>
                        <InfoValue>
                          {author.favoriteTopics.join(', ')}
                        </InfoValue>
                      </tr>
                    </tbody>
                  </InfoTable>
                </MetaCard>
              </InfoPanel>
            </ProfileGrid>

            {posts.length > 0 && (
              <PublicationsSection>
                <PublicationsHeading>
                  <PublicationsTitle>Articles on Tytum</PublicationsTitle>
                  <PublicationsSubtitle>
                    Guides and updates from this author on our site
                  </PublicationsSubtitle>
                </PublicationsHeading>
                <PostsGrid>
                  {posts.map((post) => (
                    <AuthorPostCard key={post.slug} item={post} />
                  ))}
                </PostsGrid>
              </PublicationsSection>
            )}
          </PageWrap>
        </Container>
      </Section>
    </>
  );
};

const PageWrap = styled.div`
  width: 100%;
  max-width: 100%;
  align-self: stretch;
  text-align: left;
`;

/* const AccentRule = styled.div`
  height: 4px;
  width: 100%;
  max-width: 120px;
  margin-bottom: 28px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    var(--accent-bg) 0%,
    var(--main-orange) 100%
  );
`; */

const ProfileGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-bottom: 48px;

  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: flex-start;
    gap: 40px;
  }
`;

const ProfileCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 44px 28px;
  border-radius: 16px;
  background: var(--white-color);
  min-width: 280px;
  border: 1px solid rgba(2, 136, 167, 0.18);
  box-shadow:
    0 4px 24px rgba(2, 136, 167, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: hidden;

  /* &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-bg), var(--main-orange));
  } */

  @media (min-width: 1024px) {
    width: 308px;
    flex-shrink: 0;
  }
`;

const PhotoWrapper = styled.div`
  margin-bottom: 22px;
  padding: 4px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--main-orange), var(--accent-bg));
  box-shadow: 0 8px 28px rgba(252, 161, 44, 0.25);
`;

const Photo = styled(Image)`
  border-radius: 50%;
  object-fit: cover;
  width: 200px;
  height: 200px;
  display: block;
  border: 4px solid var(--white-color);
`;

const AuthorName = styled.h1`
  color: var(--accent-bg);
  font-size: 1.625rem;
  font-weight: 700;
  text-align: center;
  margin: 0 0 6px;
  line-height: 1.25;
`;

const AuthorTitle = styled.p`
  color: var(--text-color);
  font-size: 15px;
  font-weight: 500;
  text-align: center;
  margin: 0 0 4px;
`;

const AuthorLocation = styled.p`
  color: var(--text-color-grey);
  font-size: 14px;
  text-align: center;
  margin: 0 0 16px;
`;

const SocialRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: var(--accent-bg);
  color: var(--white-color);
  transition:
    background 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
  box-shadow: 0 2px 8px rgba(2, 136, 167, 0.35);

  &:hover {
    background: var(--main-orange);
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(252, 161, 44, 0.4);
  }
`;

const InfoPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 22px;
  min-width: 0;
`;

const SectionLabel = styled.h2`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  color: var(--text-color);
  font-size: 1.25rem;
  font-weight: 700;
`;

/* const SectionLabelAccent = styled.span`
  display: inline-block;
  width: 5px;
  height: 1.35em;
  border-radius: 4px;
  background: linear-gradient(180deg, var(--main-orange), var(--accent-bg));
  flex-shrink: 0;
`; */

const Bio = styled.p`
  color: var(--text-color);
  font-size: 15px;
  line-height: 1.75;
  margin: 0;
  padding: 20px 22px;
  border-radius: 12px;
  background: var(--white-color);
  border: 1px solid rgba(252, 161, 44, 0.22);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
`;

const MetaCard = styled.div`
  border-radius: 12px;
  overflow: hidden;
  background: var(--white-color);
  border: 1px solid rgba(2, 136, 167, 0.15);
  box-shadow: 0 2px 12px rgba(2, 136, 167, 0.06);
`;

const InfoTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const InfoKey = styled.td`
  padding: 14px 18px;
  font-size: 14px;
  font-weight: 600;
  color: var(--accent-bg);
  width: 148px;
  vertical-align: top;
  border-bottom: 1px solid rgba(2, 136, 167, 0.12);
  background: rgba(255, 245, 233, 0.5);
`;

const InfoValue = styled.td`
  padding: 14px 18px;
  font-size: 14px;
  color: var(--text-color);
  vertical-align: top;
  border-bottom: 1px solid rgba(2, 136, 167, 0.1);
`;

const PublicationsSection = styled.div`
  margin-top: 8px;
  padding-top: 40px;
  border-top: 1px solid rgba(2, 136, 167, 0.15);
`;

const PublicationsHeading = styled.div`
  margin-bottom: 28px;
`;

const PublicationsTitle = styled.h2`
  margin: 0 0 8px;
  color: var(--accent-bg);
  font-size: 1.5rem;
  font-weight: 700;
`;

const PublicationsSubtitle = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: var(--text-color-grey);
  max-width: 42rem;
`;

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media only screen and (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const LinkedInIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const InstagramIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
  </svg>
);
